import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, ChevronRight } from 'lucide-react';
import { useLang } from '../i18n/LanguageContext';
import { EVENT_CONTENT, EVENT_TYPES } from '../i18n/eventContent';
import { DELIVERY_NOTICE } from '../data/deliveryNotice';
import ShareButtons from '../components/ShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://www.party-led.com';

const EventPage = () => {
  const { type } = useParams();
  const { lang, t } = useLang();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/public/products`)
      .then((res) => setProducts(res.data.slice(0, 3)))
      .catch(() => setProducts([]));
  }, []);

  if (!EVENT_TYPES.includes(type)) {
    return <Navigate to="/catalogue" replace />;
  }

  const event = EVENT_CONTENT[type];
  const c = event[lang] || event.fr;
  const path = `/evenements/${type}`;
  const delivery = DELIVERY_NOTICE[lang] || DELIVERY_NOTICE.fr;
  const faq = [...c.faq, { q: delivery.q, a: delivery.text }];

  return (
    <div className="min-h-screen bg-black pt-24 pb-20" data-testid={`event-page-${type}`}>
      <>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDescription} />
        <link rel="canonical" href={`${SITE}${path}`} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDescription} />
        <meta property="og:image" content={event.image} />
        <meta property="og:url" content={`${SITE}${path}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={event.image} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Party LED', item: SITE },
            { '@type': 'ListItem', position: 2, name: c.name, item: `${SITE}${path}` },
          ],
        })}</script>
      </>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="mb-6 text-sm text-gray-500" data-testid="event-breadcrumb">
          <Link to="/" className="hover:text-purple-400">Party LED</Link>
          <ChevronRight className="w-3 h-3 inline mx-1.5 -mt-0.5" />
          <span className="text-gray-300">{c.name}</span>
        </nav>

        {/* Hero */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-20">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-600/10 border border-purple-500/20 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
              <span className="text-purple-300 text-xs uppercase tracking-widest">{c.name}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-5" style={{ fontFamily: 'Georgia, serif' }}>
              {c.h1}
            </h1>
            <p className="text-base md:text-lg text-purple-300/90 mb-6">{c.subtitle}</p>
            <p className="text-sm md:text-base text-gray-400 leading-relaxed mb-8">{c.intro}</p>
            <Link
              to="/catalogue"
              className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-500 text-white rounded-full px-7 py-3.5 text-sm font-medium transition-all duration-300 hover:shadow-[0_0_28px_rgba(147,51,234,0.45)]"
              data-testid="event-cta"
            >
              {c.cta}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-purple-600/10 blur-3xl rounded-full" />
            <img loading="lazy"
              src={event.image}
              alt={c.h1}
              className="relative w-full aspect-[4/3] object-cover rounded-2xl border border-white/10"
              data-testid="event-hero-image"
            />
          </div>
        </div>

        {/* Benefits */}
        <div className="grid sm:grid-cols-2 gap-5 mb-20" data-testid="event-benefits">
          {c.benefits.map((b, i) => (
            <div
              key={b.slice(0, 24)}
              className="p-6 rounded-2xl bg-white/[0.03] border border-white/5 hover:border-purple-500/30 transition-colors duration-300"
            >
              <span className="block text-purple-500/60 text-3xl font-bold mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <p className="text-sm md:text-base text-gray-300 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>

        {/* Kits teaser */}
        {products.length > 0 && (
          <div className="mb-20">
            <h2 className="text-base md:text-lg font-semibold text-white mb-6 uppercase tracking-widest">{t.nav.kits}</h2>
            <div className="grid sm:grid-cols-3 gap-5" data-testid="event-kits">
              {products.map((p) => (
                <Link
                  key={p.id}
                  to={`/produit/${p.slug}`}
                  className="group rounded-2xl overflow-hidden bg-gray-900 border border-white/5 hover:border-purple-500/40 transition-all duration-300"
                >
                  <img loading="lazy"
                    src={p.images?.[0]}
                    alt={p.name}
                    className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="p-5">
                    <p className="text-white text-sm font-medium mb-1">{p.name}</p>
                    <p className="text-purple-400 text-sm">{p.price.toLocaleString('fr-FR')} €</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* FAQ */}
        <div className="mb-16">
          <h2 className="text-base md:text-lg font-semibold text-white mb-6 uppercase tracking-widest">FAQ</h2>
          <div className="space-y-4" data-testid="event-faq">
            {faq.map((f) => (
              <div key={f.q} className="p-6 rounded-2xl bg-white/[0.03] border border-white/5">
                <p className="text-white text-sm md:text-base font-medium mb-2">{f.q}</p>
                <p className="text-gray-400 text-sm leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-8 border-t border-white/5">
          <ShareButtons path={path} title={c.h1} />
        </div>
      </div>
    </div>
  );
};

export default EventPage;
